import React, { useCallback } from 'react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIProactivePluginInjectionAPI } from '../../aiProactivePluginType';
import { toggleProactiveAISuggestionDisplayWithAnalytics } from '../../pm-plugins/ai-proactive/commands-with-analytics';
import type { AIGlobalOptIn } from '../../types';
import { PrimaryToolbarButton } from '../components/PrimaryToolbarButton/PrimaryToolbarButton';
// Remove below line while cleaning platform_editor_ai_object_sidebar_injection

type PrimaryToolbarComponentType = {
	api: AIProactivePluginInjectionAPI;
	aiGlobalOptIn: AIGlobalOptIn;
	// Make toggleSuggestionsPanel mandatory prop while cleaning platform_editor_ai_object_sidebar_injection
	toggleSuggestionsPanel?: (triggeredFrom: string) => void;
	editorView: EditorView;
	disabled: boolean;
};

const PrimaryToolbarComponent = ({
	api,
	aiGlobalOptIn,
	toggleSuggestionsPanel,
	disabled,
	editorView,
}: PrimaryToolbarComponentType) => {
	const { connectivityState } = useSharedPluginState(api, ['connectivity']);

	const isProactiveContextPanelOpen = useSharedPluginStateSelector(
		api,
		'aiProactive.isProactiveContextPanelOpen',
	);
	const showNewRecommendationsNotification = useSharedPluginStateSelector(
		api,
		'aiProactive.showNewRecommendationsNotification',
	);
	const unreadRecommendationCount = useSharedPluginStateSelector(
		api,
		'aiProactive.unreadRecommendationCount',
	);

	const onClick = useCallback(() => {
		// TODO: handle aiGlobalOptIn correctly
		// Make toggleSuggestionsPanel will be mandatory prop after platform_editor_ai_object_sidebar_injection is cleaned
		//	so no need to check for toggleSuggestionsPanel
		if (toggleSuggestionsPanel && fg('platform_editor_ai_object_sidebar_injection')) {
			toggleSuggestionsPanel('mainToolbar');
		} else {
			const { state, dispatch } = editorView;
			toggleProactiveAISuggestionDisplayWithAnalytics(api?.analytics?.actions)('mainToolbar')(
				state,
				dispatch,
			);
		}
	}, [api?.analytics?.actions, editorView, toggleSuggestionsPanel]);

	const showLoading = !isProactiveContextPanelOpen && !!showNewRecommendationsNotification;

	const isOffline = connectivityState?.mode === 'offline' || false;

	return (
		<>
			<PrimaryToolbarButton
				api={api}
				onClick={onClick}
				disabled={disabled || isOffline}
				selected={!!isProactiveContextPanelOpen}
				loading={showLoading}
				unreadCount={unreadRecommendationCount}
				view={editorView}
			/>
		</>
	);
};

export const getAIPrimaryToolbarComponent =
	({
		api,
		aiGlobalOptIn,
		toggleSuggestionsPanel,
	}: {
		api: AIProactivePluginInjectionAPI;
		aiGlobalOptIn: AIGlobalOptIn;
		toggleSuggestionsPanel: (triggeredFrom: string) => void;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
				toggleSuggestionsPanel={toggleSuggestionsPanel}
			/>
		);
	};

// Remove below function while cleaning platform_editor_ai_object_sidebar_injection
export const getAIPrimaryToolbarComponentOld =
	({
		api,
		aiGlobalOptIn,
	}: {
		api: AIProactivePluginInjectionAPI;
		aiGlobalOptIn: AIGlobalOptIn;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
			/>
		);
	};
